/**
 * Tweened keyframe clips: a sequence of sheet frames, each with a transform key. The frame holds
 * until the next key; the transform (scale, offset, rotation, alpha) tweens toward the next key.
 * Pure functions (unit-tested); sampling is by time (ms) or by a 0..1 phase for sim-synced cycles.
 * Render-side only: nothing here reads or touches sim state.
 */
import { type Ease, Easing } from './tween.ts';

export interface Key {
  /** Sheet frame (atlas name) shown from this key until the next. */
  frame: string;
  /** Time of this key as a fraction 0..1 of the clip. Keys must be ascending; first at 0. */
  at: number;
  sx?: number;
  sy?: number;
  /** Offsets in cell units (multiplied by the cell size by the caller). */
  dx?: number;
  dy?: number;
  rot?: number;
  alpha?: number;
}

export interface Clip {
  keys: Key[];
  /** Nominal duration in ms (used for time-driven playback). */
  duration: number;
  loop?: boolean;
  ease?: Ease;
}

export interface Pose {
  frame: string;
  sx: number;
  sy: number;
  dx: number;
  dy: number;
  rot: number;
  alpha: number;
}

const ID: Required<Omit<Key, 'frame' | 'at'>> = { sx: 1, sy: 1, dx: 0, dy: 0, rot: 0, alpha: 1 };

/** Pose at phase p (0..1). Looping clips tween the last key back toward the first. */
export function samplePhase(clip: Clip, p: number): Pose {
  const keys = clip.keys;
  if (keys.length === 0) throw new Error('empty clip');
  const t = clip.loop ? ((p % 1) + 1) % 1 : Math.max(0, Math.min(1, p));
  let i = 0;
  while (i + 1 < keys.length && keys[i + 1].at <= t) i++;
  const a = keys[i];
  const b = i + 1 < keys.length ? keys[i + 1] : clip.loop ? { ...keys[0], at: 1 } : a;
  const span = b.at - a.at;
  const k = span > 0 ? (clip.ease ?? Easing.linear)(Math.min(1, (t - a.at) / span)) : 1;
  const lerp = (f: keyof typeof ID): number => (a[f] ?? ID[f]) + ((b[f] ?? ID[f]) - (a[f] ?? ID[f])) * k;
  return { frame: a.frame, sx: lerp('sx'), sy: lerp('sy'), dx: lerp('dx'), dy: lerp('dy'), rot: lerp('rot'), alpha: lerp('alpha') };
}

/** Pose at elapsed ms; non-looping clips hold their last key. */
export function sampleTime(clip: Clip, ms: number): Pose {
  return samplePhase(clip, ms / Math.max(1, clip.duration));
}

/** Evenly spaced frames, optionally with a per-frame transform, into a clip. */
export function frames(names: readonly string[], duration: number, opts: { loop?: boolean; ease?: Ease; key?: (i: number) => Omit<Key, 'frame' | 'at'> } = {}): Clip {
  const n = names.length;
  return { duration, loop: opts.loop, ease: opts.ease, keys: names.map((frame, i) => ({ frame, at: i / n, ...(opts.key?.(i) ?? {}) })) };
}

/** Time-driven playback of one-shot / looping clips on a sprite-like target. */
export class ClipPlayer {
  clip: Clip | null = null;
  private t = 0;
  private hold = false;
  private onDone: (() => void) | null = null;

  /** Start a clip. `hold` keeps a finished one-shot on its last key until stop() (e.g. crushed). */
  play(clip: Clip, opts: { onDone?: () => void; hold?: boolean } = {}): void {
    this.clip = clip;
    this.t = 0;
    this.hold = !!opts.hold;
    this.onDone = opts.onDone ?? null;
  }

  /** Queue a clip to play when the current one-shot finishes (replaces any earlier queued clip). */
  then(clip: Clip): void {
    if (!this.clip) return this.play(clip);
    this.onDone = () => this.play(clip);
  }

  stop(): void {
    this.clip = null;
    this.onDone = null;
  }

  get playing(): boolean {
    return this.clip !== null;
  }

  /** Advance and return the current pose, or null when idle. Fires onDone once a one-shot ends. */
  advance(dt: number): Pose | null {
    const c = this.clip;
    if (!c) return null;
    this.t += dt;
    if (!c.loop && this.t >= c.duration) {
      const pose = samplePhase(c, 1);
      const done = this.onDone;
      this.onDone = null;
      if (!this.hold) this.stop();
      done?.();
      return pose;
    }
    return sampleTime(c, this.t);
  }
}
